import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { map } from 'rxjs';

import { LINKED_ITEM_ENTITY_TYPE } from '@ninety/platform/models/analytics/segment/linked-item-entity-type.map';
import { SegmentTrackEvent } from '@ninety/platform/models/analytics/segment/segment-track-event.enum';
import { SegmentActions } from '@ninety/platform/state/app-global/analytics/segment/segment.actions';

import { LinkedItemsActions } from './linked-items.actions';
import { LinkedItemsSelectors } from './linked-items.selectors';

@Injectable()
export class LinkedItemsAnalyticsEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);

  /** These events are polymorphic, so `entityType` / `parentEntity` stay at the call site rather
   *  than in SEGMENT_EVENT_METADATA. They are translated through LINKED_ITEM_ENTITY_TYPE on the way
   *  out: the raw `LinkedItemTypeEnum` is the linked-items feature's own vocabulary and six of its
   *  values are not schema `EntityType` values, which left the same entity kind reported under two
   *  different names depending on which feature emitted it. */
  trackLinkedItemOpened$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LinkedItemsActions.openLinkedItem),
      concatLatestFrom(() => [
        this.store.select(LinkedItemsSelectors.selectId),
        this.store.select(LinkedItemsSelectors.selectType),
      ]),
      map(([{ linkedItemType, linkedItemId }, parentEntityID, parentEntity]) =>
        SegmentActions.track({
          event: SegmentTrackEvent.LINKED_ITEM_OPENED,
          params: {
            entityId: linkedItemId,
            entityType: LINKED_ITEM_ENTITY_TYPE[linkedItemType],
            parentEntityID,
            parentEntity: LINKED_ITEM_ENTITY_TYPE[parentEntity],
          },
        })
      )
    )
  );

  trackLinkedItemCreated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LinkedItemsActions.createdLinkedItem),
      map(({ linkedItem: { id, linkedItemType, linkedItemId }, createdFrom }) =>
        SegmentActions.track({
          event: SegmentTrackEvent.LINKED_ITEM_CREATED,
          params: {
            entityId: linkedItemId,
            entityType: LINKED_ITEM_ENTITY_TYPE[linkedItemType],
            parentEntityID: id,
            parentEntity: LINKED_ITEM_ENTITY_TYPE[createdFrom],
          },
        })
      )
    )
  );

  /** `toTypes` is de-duplicated after normalization, not before — a batch holding both a
   *  learningSubject and a learningTopic is one `topic` type, not two. */
  trackExistingItemsLinked$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LinkedItemsActions.addLinkedItems),
      map(({ linkedItems, fromId, fromType }) =>
        SegmentActions.track({
          event: SegmentTrackEvent.LINKED_ITEM_LINK_EXISTING_ITEM,
          params: {
            parentEntityID: fromId,
            parentEntity: LINKED_ITEM_ENTITY_TYPE[fromType],
            toTypes: [...new Set(linkedItems.map(item => LINKED_ITEM_ENTITY_TYPE[item.toType]))].join(', '),
          },
        })
      )
    )
  );
}
